import { z } from "zod";
import { currentBotConfigSchema } from "../config/migrate.js";
import { themeInputSchema } from "../theme.js";
import { allowedOriginSchema } from "../origin.js";

/**
 * The agency admin's view of a bot — never sent to the widget (see
 * `messageDtoSchema` / `SessionReady` for what the widget gets instead).
 * Excludes `orgId` (implicit from the URL the panel is already scoped to).
 */
export const botDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  status: z.enum(["draft", "active", "disabled"]),
  provider: z.string(),
  model: z.string(),
  fallbackModel: z.string().nullable(),
  locale: z.string(),
  hasPublishedVersion: z.boolean(),
  createdAt: z.string().datetime(),
});
export type BotDto = z.infer<typeof botDtoSchema>;

/**
 * Always the *current* config schema shape — every read is migrated first
 * (see PLAN.md "Stored config is versioned and migrated on read"), so the
 * admin panel never has to know a row's `config_schema_version`.
 */
export const botVersionDtoSchema = z.object({
  id: z.string().uuid(),
  version: z.number().int(),
  config: currentBotConfigSchema,
  theme: themeInputSchema,
  publishedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type BotVersionDto = z.infer<typeof botVersionDtoSchema>;

export const botKeyDtoSchema = z.object({
  id: z.string().uuid(),
  publicKey: z.string(),
  allowedOrigins: z.array(z.string()),
  revokedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type BotKeyDto = z.infer<typeof botKeyDtoSchema>;

// --- Request bodies ------------------------------------------------------

export const createBotBodySchema = z.object({
  name: z.string().min(1),
  slug: z
    .string()
    .min(1)
    .regex(/^[a-z0-9-]+$/, "slug must be lowercase letters, numbers and hyphens"),
  model: z.string().min(1),
  fallbackModel: z.string().min(1).optional(),
  locale: z.string().min(2).optional(),
});
export type CreateBotBody = z.infer<typeof createBotBodySchema>;

/**
 * One entry of the provider's own model list. Read from the API rather
 * than kept in a table here: a hardcoded list is wrong the day a model
 * ships or an account loses access to one, and both failures land on the
 * admin as an unexplained turn error rather than a missing option.
 */
export const providerModelDtoSchema = z.object({
  id: z.string(),
  /** Best-effort: embedding/speech/image families are excluded by name. */
  chat: z.boolean(),
  createdAt: z.string().nullable(),
});
export type ProviderModelDto = z.infer<typeof providerModelDtoSchema>;

export const listProviderModelsResponseSchema = z.object({
  provider: z.string(),
  models: z.array(providerModelDtoSchema),
});
export type ListProviderModelsResponse = z.infer<typeof listProviderModelsResponseSchema>;

export const updateBotBodySchema = z.object({
  name: z.string().min(1).optional(),
  status: z.enum(["draft", "active", "disabled"]).optional(),
  model: z.string().min(1).optional(),
  fallbackModel: z.string().min(1).nullable().optional(),
  locale: z.string().min(2).optional(),
});
export type UpdateBotBody = z.infer<typeof updateBotBodySchema>;

export const updateDraftBodySchema = z.object({
  config: currentBotConfigSchema,
  theme: themeInputSchema,
});
export type UpdateDraftBody = z.infer<typeof updateDraftBodySchema>;

export const rollbackBodySchema = z.object({ versionId: z.string().uuid() });
export type RollbackBody = z.infer<typeof rollbackBodySchema>;

export const mintBotKeyBodySchema = z.object({ allowedOrigins: z.array(allowedOriginSchema) });
export type MintBotKeyBody = z.input<typeof mintBotKeyBodySchema>;

/** Replaces a live key's allowlist in place — the public key itself, already pasted into a site's template, stays the same. */
export const updateBotKeyOriginsBodySchema = z.object({ allowedOrigins: z.array(allowedOriginSchema) });
export type UpdateBotKeyOriginsBody = z.input<typeof updateBotKeyOriginsBodySchema>;

export const setProviderCredentialBodySchema = z.object({ provider: z.string().min(1), apiKey: z.string().min(1) });
export type SetProviderCredentialBody = z.infer<typeof setProviderCredentialBodySchema>;

/**
 * What the panel gets back when an agency admin previews a bot's live
 * widget. The token is the same short-lived widget session JWT a shopper's
 * browser would have minted for itself with a public key — the preview just
 * mints it through an authenticated route instead, because the panel's
 * origin is deliberately not on any bot key's allowlist. `conversationId`
 * is returned so the panel can link straight to the conversation the
 * preview is writing into: a preview is a real conversation, in the inbox,
 * like every other.
 */
export const widgetPreviewSessionResponseSchema = z.object({
  token: z.string().min(1),
  conversationId: z.string().uuid(),
  visitorId: z.string().uuid(),
});
export type WidgetPreviewSessionResponse = z.infer<typeof widgetPreviewSessionResponseSchema>;

/**
 * Everything the panel's *public* preview page (`/preview/:botId/:token`)
 * renders — served unauthenticated from the published version, so a link
 * an agency sent last week shows the bot as it is configured today.
 *
 * Deliberately narrow: the name, the greeting and the theme a shopper
 * would meet anyway on the client's own site, plus capability bullets
 * derived from the config (see `describeBotFeatures`). Never the prompt,
 * never the tool list verbatim, never anything about the org — the link is
 * shareable, so its payload has to be safe in a stranger's hands.
 */
export const publicPreviewDtoSchema = z.object({
  botId: z.string().uuid(),
  botName: z.string(),
  locale: z.string(),
  greeting: z.string().optional(),
  theme: themeInputSchema,
  features: z.array(z.object({ id: z.string(), title: z.string(), description: z.string() })),
});
export type PublicPreviewDto = z.infer<typeof publicPreviewDtoSchema>;

export const publicPreviewSessionBodySchema = z.object({
  publicKey: z.string().min(1),
  visitorKey: z.string().min(1),
  entryUrl: z.string().url().optional(),
});
export type PublicPreviewSessionBody = z.infer<typeof publicPreviewSessionBodySchema>;

/** Same shape as the authenticated preview mint — the widget can't tell the two apart, which is the point. */
export const publicPreviewSessionResponseSchema = z.object({
  token: z.string().min(1),
  conversationId: z.string().uuid(),
});
export type PublicPreviewSessionResponse = z.infer<typeof publicPreviewSessionResponseSchema>;
